import React, { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

function getCentroid(d) {
  const nums = d.replace(/[A-Za-z]/g, " ").trim().split(/[\s,]+/).map(Number);
  let xs = [];
  let ys = [];

  for (let i = 0; i < nums.length; i += 2) {
    xs.push(nums[i]);
    ys.push(nums[i + 1]);
  }

  return {
    cx: xs.reduce(function (a, b) { return a + b; }, 0) / xs.length,
    cy: ys.reduce(function (a, b) { return a + b; }, 0) / ys.length
  };
}

function toUtilPercent(row) {
  if (row.occupiedFlag === 1) return 100;
  if (row.signsOfLifeFlag === 1) return 50;
  return 0;
}

function calcCategoryStats(series) {
  if (!series || series.length === 0) {
    return { avg: 0, max: 0, maxDate: "", min: 0, minDate: "", numericSeries: [] };
  }

  const groups = {};

  series.forEach(function (p) {
    if (!groups[p.date]) groups[p.date] = [];
    groups[p.date].push(p.value);
  });

  const timestampAverages = Object.keys(groups).map(function (ts) {
    const arr = groups[ts];
    const avg = arr.reduce(function (a, b) { return a + b; }, 0) / arr.length;
    return { ts: ts, avg: avg };
  });

  timestampAverages.sort(function (a, b) {
    return new Date(a.ts) - new Date(b.ts);
  });

  let maxObj = timestampAverages[0];
  let minObj = timestampAverages[0];

  timestampAverages.forEach(function (pt) {
    if (pt.avg > maxObj.avg) maxObj = pt;
    if (pt.avg < minObj.avg) minObj = pt;
  });

  const total = series.reduce(function (acc, p) {
    return acc + p.value;
  }, 0);

  const overallAvg = Math.round(total / series.length);

  return {
    avg: overallAvg,
    max: Math.round(maxObj.avg),
    maxDate: maxObj.ts,
    min: Math.round(minObj.avg),
    minDate: minObj.ts,
    numericSeries: timestampAverages.map(function (p) {
      return Math.round(p.avg);
    })
  };
}

export default function FloorSvg(props) {
	
	
	const [hoverDesk, setHoverDesk] =
	    useState(null);
  const dragRef = useRef({
    moved: false,
    startX: 0,
    startY: 0
  });

  const suppressClickRef = useRef(false);

  const clickRef = useRef({
    startX: 0,
    startY: 0,
    startTime: 0
  });

  const {
    selectedDesk,
    setSelectedDesk,
    data,
    desks,
    mode,
    intensityMap,
    frame,
    floorWidth,
    floorHeight,
    floorImage,
    activities
  } = props;

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const transformRef = useRef(null);

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  
  
  function buildTooltipData(desk) {

      return {
          code:
              desk.spaceCode || "Unknown",

          capacity:
              desk.capacity || 1,

          organisation:
              desk.organisationName ||
              "Not Allocated",

          colour:
              desk.organisationColor ||
              "#999999"
      };

  }

  function applyFakeFlicker(intensity) {
    if (frame && frame.fakeAnimationSeed) {
      return intensity * (0.7 + Math.random() * 0.6);
    }

    return intensity;
  }

  function handleDeskClick(desk) {
    const deskRows = data.filter(function (r) {
      return r.spaceId === Number(desk.spaceId);
    });

    const series = deskRows.map(function (r) {
      return {
        value: toUtilPercent(r),
        date: r.observationTs
      };
    });

    const stats = calcCategoryStats(series);
    const centroid = getCentroid(desk.svg);

    setSelectedDesk({
      id: desk.spaceId,
      code: desk.spaceCode,
      centroid: { x: centroid.cx, y: centroid.cy },
      stats: stats
    });
  }

  useEffect(function () {
    const parsedWidth = Number(floorWidth);
    const parsedHeight = Number(floorHeight);

    if (!parsedWidth || !parsedHeight) return;

    setWidth(parsedWidth);
    setHeight(parsedHeight);
  }, [floorWidth, floorHeight]);

  useEffect(function () {
    if (!containerRef.current) return;
    if (!svgRef.current) return;
    if (!transformRef.current) return;

    const container = containerRef.current;
    const svg = svgRef.current;

    function fit() {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const bbox = svg.getBBox();

      if (!bbox.width || !bbox.height) return;

      const scale = Math.min(cw / bbox.width, ch / bbox.height) * 0.9;
      const x = (cw - bbox.width * scale) / 2 - bbox.x * scale;
      const y = (ch - bbox.height * scale) / 2 - bbox.y * scale;

      transformRef.current.setTransform(x, y, scale, 200);
    }

    setTimeout(fit, 50);
  }, [width, height, desks]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transform: frame && frame.fakeAnimationSeed ? "scale(1.003)" : "scale(1)",
        transition: "transform 0.12s ease-out"
      }}
      onMouseDown={(e) => {
        dragRef.current.moved = false;
        suppressClickRef.current = false;
        dragRef.current.startX = e.clientX;
        dragRef.current.startY = e.clientY;
      }}
      onMouseMove={(e) => {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;

        if ((dx * dx + dy * dy) > 9) {
          dragRef.current.moved = true;
        }
      }}
      onMouseUp={() => {
        if (dragRef.current.moved) {
          suppressClickRef.current = true;

          setTimeout(() => {
            suppressClickRef.current = false;
          }, 100);
        }
      }}
    >
      {!width || !height ? (
        <div style={{ padding: "20px" }}>Loading floor...</div>
      ) : (
        <TransformWrapper
          ref={transformRef}
          minScale={0.02}
          maxScale={1}
          wheel={{
            step: 0.01,
            smoothStep: 0.001,
            wheelEnabled: true,
            touchPadEnabled: false
          }}
          pinch={{
            step: 5
          }}
          doubleClick={{
            disabled: true
          }}
        >
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ width: width, height: height }}
          >
            <svg ref={svgRef} width={width} height={height} style={{ display: "block" }}>
              <defs>
                <filter id="gauss-soft">
                  <feGaussianBlur stdDeviation="18" />
                </filter>
              </defs>

              {floorImage && (
                <image
                  href={floorImage}
                  xlinkHref={floorImage}
                  width={width}
                  height={height}
                  preserveAspectRatio="none"
                />
              )}

              {desks.map(function (desk) {
                const isSelected = selectedDesk && selectedDesk.id === desk.spaceId;
                const centroid = getCentroid(desk.svg);

                if (mode === "gaussian-rag") {
                  let occ = null;

                  if (frame && frame.occupancyMap) {
                    occ = frame.occupancyMap[desk.spaceId];
                  }

                  let intensity = 0;

                  if (occ) {
                    if (occ.occupiedFlag === 1) intensity = 1;
                    else if (occ.signsOfLifeFlag === 1) intensity = 0.5;
                  } else {
                    intensity = intensityMap[desk.spaceId] || 0;
                  }

                  intensity = applyFakeFlicker(intensity);

                  if (!intensity) return null;

                  const radius = Math.max(width * 0.02 * intensity, 20);
                  const opacity = 0.15 + (intensity * 0.35);

                  return (
                    <circle
                      key={"gauss-" + desk.spaceId}
                      cx={centroid.cx}
                      cy={centroid.cy}
                      r={radius}
                      fill="red"
                      opacity={opacity}
                      filter="url(#gauss-soft)"
                      onMouseDown={(e) => {
                        clickRef.current.startX = e.clientX;
                        clickRef.current.startY = e.clientY;
                        clickRef.current.startTime = Date.now();
                      }}
                      onMouseUp={(e) => {
                        const dx = e.clientX - clickRef.current.startX;
                        const dy = e.clientY - clickRef.current.startY;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        const duration = Date.now() - clickRef.current.startTime;

                        if (dist < 5 && duration < 250) {
                          handleDeskClick(desk);
                        }
                      }}
                    />
                  );
                }

                if (mode === "activity") {
                  let activityId = null;

                  if (frame && frame.occupancyMap && frame.occupancyMap[desk.spaceId]) {
                    activityId = frame.occupancyMap[desk.spaceId].activityId;
                  }

                  const act = activities.find(function (a) {
                    return a.id === Number(activityId);
                  });

                  const fill = act ? act.color : "rgba(200,200,200,0.4)";

                  return (
                    <path
                      key={"desk-" + desk.spaceId}
                      d={desk.svg}
                      fill={fill}
                      stroke={isSelected ? "#0078ff" : "rgba(0,0,0,0.25)"}
                      strokeWidth={isSelected ? 4 : 1.5}
                      onMouseDown={(e) => {
                        clickRef.current.startX = e.clientX;
                        clickRef.current.startY = e.clientY;
                        clickRef.current.startTime = Date.now();
                      }}
                      onMouseUp={(e) => {
                        const dx = e.clientX - clickRef.current.startX;
                        const dy = e.clientY - clickRef.current.startY;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        const duration = Date.now() - clickRef.current.startTime;

                        if (dist < 5 && duration < 250) {
                          handleDeskClick(desk);
                        }
                      }}
                    />
                  );
                }

                if (mode === "organisation") {
                  let intensity = 0;

                  if (frame && frame.occupancyMap && frame.occupancyMap[desk.spaceId]) {
                    const occ = frame.occupancyMap[desk.spaceId];

                    if (occ.occupiedFlag === 1) {
                      intensity = 1;
                    } else if (occ.signsOfLifeFlag === 1) {
                      intensity = 0.5;
                    }
                  } else {
                    intensity = intensityMap[desk.spaceId] || 0;
                  }

                  intensity = applyFakeFlicker(intensity);

                  const opacity = 0.15 + (intensity * 0.85);

                  const fill = desk.organisationColor ? desk.organisationColor : "#cccccc";

                  return (
                    <path
					style={{
					    cursor: "pointer"
					}}
                      key={"desk-" + desk.spaceId}
                      d={desk.svg}
                      fill={fill}
                      fillOpacity={opacity}
                      stroke={isSelected ? "#0078ff" : "rgba(0,0,0,0.25)"}
                      strokeWidth={isSelected ? 4 : 1.5}
					  onMouseDown={(e) => {

					      clickRef.current.startX =
					          e.clientX;

					      clickRef.current.startY =
					          e.clientY;

					      clickRef.current.startTime =
					          Date.now();

					  }}

					  onMouseUp={(e) => {

					      const dx =
					          e.clientX -
					          clickRef.current.startX;

					      const dy =
					          e.clientY -
					          clickRef.current.startY;

					      const dist =
					          Math.sqrt(dx * dx + dy * dy);

					      const duration =
					          Date.now() -
					          clickRef.current.startTime;

					      if (
					          dist < 5 &&
					          duration < 250
					      ) {

					          handleDeskClick(desk);

					      }

					  }}

					  onMouseEnter={(e) => {

					      setHoverDesk({
					          x: e.clientX,
					          y: e.clientY,
					          ...buildTooltipData(desk)
					      });

					  }}

					  onMouseMove={(e) => {

					      setHoverDesk(function (prev) {

					          if (!prev) {
					              return null;
					          }

					          return {
					              ...prev,
					              x: e.clientX,
					              y: e.clientY
					          };

					      });

					  }}

					  onMouseLeave={() => {

					      setHoverDesk(null);

					  }}
                    />
                  );
                }

                let intensity = 0;

                if (frame && frame.occupancyMap && frame.occupancyMap[desk.spaceId]) {
                  const occ = frame.occupancyMap[desk.spaceId];

                  if (occ.occupiedFlag === 1) intensity = 1;
                  else if (occ.signsOfLifeFlag === 1) intensity = 0.5;
                } else {
                  intensity = intensityMap[desk.spaceId] || 0;
                }

                intensity = applyFakeFlicker(intensity);

                return (
                  <path
                    key={"desk-" + desk.spaceId}
                    d={desk.svg}
                    fill={"rgba(255,0,0," + intensity + ")"}
                    stroke={isSelected ? "#0078ff" : "rgba(0,0,0,0.3)"}
                    strokeWidth={isSelected ? 4 : 2}
                    onMouseDown={(e) => {
                      clickRef.current.startX = e.clientX;
                      clickRef.current.startY = e.clientY;
                      clickRef.current.startTime = Date.now();
                    }}
                    onMouseUp={(e) => {
                      const dx = e.clientX - clickRef.current.startX;
                      const dy = e.clientY - clickRef.current.startY;
                      const dist = Math.sqrt(dx * dx + dy * dy);
                      const duration = Date.now() - clickRef.current.startTime;

                      if (dist < 5 && duration < 250) {
                        handleDeskClick(desk);
                      }
                    }}
					style={{
					    cursor: "pointer"
					}}

					onMouseEnter={(e) => {

					    setHoverDesk({
					        x: e.clientX,
					        y: e.clientY,
					        code:
					            desk.spaceCode || "Unknown",
					        capacity:
					            desk.capacity || 1
					    });

					}}

					onMouseMove={(e) => {

					    setHoverDesk(function (prev) {

					        if (!prev) {
					            return null;
					        }

					        return {
					            ...prev,
					            x: e.clientX,
					            y: e.clientY
					        };

					    });

					}}

					onMouseLeave={() => {

					    setHoverDesk(null);

					}}
                  />
                );
              })}
            </svg>
          </TransformComponent>
        </TransformWrapper>
      )}
	  {hoverDesk && (

	      <div
	          style={{
	              position: "fixed",
	              left: hoverDesk.x - 80,
	              top: hoverDesk.y - 160,
	              background:
	                  "rgba(15,23,42,0.96)",
	              color: "#ffffff",
	              padding: "12px 14px",
	              borderRadius: "12px",
	              fontSize: "13px",
	              pointerEvents: "none",
	              zIndex: 99999,
	              minWidth: "180px",
	              boxShadow:
	                  "0 8px 28px rgba(0,0,0,0.35)",
	              border:
	                  "1px solid rgba(255,255,255,0.08)",
	              backdropFilter:
	                  "blur(8px)"
	          }}
	      >

		  <div
		      style={{
		          fontWeight: 700,
		          marginBottom: "8px"
		      }}
		  >
		      {hoverDesk.code}
		  </div>

		  {hoverDesk.organisation && (

		      <div
		          style={{
		              display: "flex",
		              alignItems: "center",
		              marginBottom: "6px"
		          }}
		      >

		          <div
		              style={{
		                  width: "10px",
		                  height: "10px",
		                  borderRadius: "50%",
		                  background:
		                      hoverDesk.colour ||
		                      "#999999",
		                  marginRight: "8px"
		              }}
		          />

		          {hoverDesk.organisation}

		      </div>

		  )}

		  <div
		      style={{
		          opacity: 0.85
		      }}
		  >
		      Capacity: {hoverDesk.capacity}
		  </div>

	      </div>

	  )}
    </div>
  );
}